// ABOUTME: Relation data tab listing the sales channels of a single relation.
// ABOUTME: Row click opens the channel sidepanel, the action button opens the full channel page.
import { Box, IconButton, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { tokens } from "../../../../../theme";
import Text from "../../../../../components/theme/text/Text";
import ShDataGrid3 from "../../../../../components/theme/datagrid3/ShDataGrid3";
import ShDataGridSelectedActions from "../../../../../components/theme/datagrid2/ShDataGridSelectedActions";
import DropdownButton from "../../../../../components/theme/buttons/DropdownButton";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import ShLabelSkeleton from "../../../../../components/theme/skeleton/ShLabelSkeleton";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import SidepanelLoader from "../../../../../components/theme/sidepanel2/SidepanelLoader";
import MLoader from "../../../../../components/global/Modals/MLoader";
import { RelationWebshopIntegrationCard } from "../../../../../components/global/cards/other/WebshopIntegrationCard";
import { useBulkUpdateWebshopCredentialsMutation, useGetWebshopCredentialsQuery } from "../../../../../newapi/webshop/webshopSlice";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import { general_states } from "../../../../../utils/staticEnums";
import { getWebshopLogo } from "../../../../../utils/logomapper";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";

const SpWebshopCredentialsDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpWebshopCredentialsDetails/SpWebshopCredentialsDetails"));
const SpEtailizeWebshopDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpEtailizeWebshopDetails/SpEtailizeWebshopDetails"));

const GridActions = (props) => {
    return <></>;
}

const gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const Saleschannels = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [isExtended,setIsExtended] = useState(false);
    const [isExtendedEtailize,setIsExtendedEtailize] = useState(false);
    const [spLoading, setSpLoading] = useState();

    const hasModifyPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.WEBSHOPCREDENTIALS_MANAGE])
    );

    const [webshopCredentialsId, setWebshopCredentialsId] = useState();
    const webshopCredentialsIdRef = useRef(webshopCredentialsId);

    const getWebshopCredentialDetails = (params) => {
        if (!params) return;

        const newwebshopCredentialsId = parseInt(params.row.id);
        // Only update state if the webshopCredentialsId has actually changed
        if (webshopCredentialsIdRef.current !== newwebshopCredentialsId) {
            if(!spLoading) {
                setSpLoading(true);
            }
            setWebshopCredentialsId(newwebshopCredentialsId);
            webshopCredentialsIdRef.current = newwebshopCredentialsId; // Update the ref to the new value
        }
        if(params.row.integrator){
            setIsExtendedEtailize(true);
        } else {
            setIsExtended(true);
        }
    };

    // The full channel page carries the syncs, logs and product views that do not fit a sidepanel.
    // It opens in its own tab so the relation stays put behind it.
    const openChannelPage = (event, id) => {
        event.stopPropagation();
        window.open(`/warehouse/webshopcredentials/data?webshop=${id}`, '_blank', 'noopener,noreferrer');
    };

    const [selectedItems, setSelectedItems] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [bulkUpdateWebshopCredentials, { isLoading: isBulkUpdating }] = useBulkUpdateWebshopCredentialsMutation();

    const handleActivateSelected = async () => {
        if (selectedItems.length === 0) return;
        if (isBulkUpdating) return;
        setIsUpdating(true);

        try {
            const response = await bulkUpdateWebshopCredentials({
                ids: selectedItems,
                state: general_states.ACTIVE
            }).unwrap();

            setSelectedItems([]); // Clear selection after successful update
            showToastMessageUpdateRequest(t, response);
        } catch (error) {
            showToastMessageRequestError(t("update_failed"), t, error);
        } finally {
            setIsUpdating(false);
        }
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions:gridOptions,
            mobileActions:<GridActions></GridActions>
        });

        return () => {
            // console.log("unmounting saleschannels");
        }
    },[]);

    const columns = [
        {field: "description", headerName: t("channel"), flex:2, cellClassName: "webshop", renderCell : (cellValues) => {
            if(cellValues.row.skeleton) {
                return <ShTextSkeleton/>
            }

            let name = cellValues.row.webshop_type;
            if(cellValues.row.webshop_type === "etailize") {
                name = cellValues.row.credentials || cellValues.row.webshop_type;
            }
            const webshopLogo = getWebshopLogo(cellValues.row.webshop_type,{theme,colors},name);

            return (
                <>
                <Box sx={{ paddingRight: '10px' }}>
                    {webshopLogo}
                </Box>
                <Box display="flex" flexDirection="column">
                    <Text semibold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{cellValues.value}</Text>
                    {cellValues.row.integrator && <Text light>{cellValues.row.integrator?.name}</Text>}
                </Box>
                </>
            )
        }},
        {field: "webshop_type", headerName: t("webshop_type"), flex:1.5, renderCell : (cellValues) => {
            if(cellValues.row.skeleton) {
                return <ShTextSkeleton/>
            }

            return (
                <Text>{cellValues.value}</Text>
            )
        }},
        {
            field: "state",
            headerName: t("status"),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShLabelSkeleton/>
                }

                const labelColor = params.row.state === "active" ? "green" : theme.palette.mode === "dark" ? "greyLight" : "grey";

                return (
                    <ShLabel size={32} palette="normal" variant={labelColor}>
                        {t(params.row.state)}
                    </ShLabel>
                );
            }
        },
        {field: "createdAt", headerName: t("created_at"), flex:1, renderCell : (cellValues) => {
            if(cellValues.row.skeleton) {
                return <ShTextSkeleton/>
            }

            let date = new Date(cellValues.row.createdAt).toLocaleDateString(i18n.language);

            return (
                <Text>{date}</Text>
            )
        }},
        {
            field: "actions",
            headerName: "",
            width: 64,
            sortable: false,
            renderCell: (cellValues) => {
                // A concept channel has no synced data yet, so its page would come up empty.
                if(cellValues.row.skeleton || cellValues.row.state === general_states.CONCEPT) {
                    return <></>
                }

                return (
                    <IconButton
                        title={t("open_channel_details")}
                        onClick={(event) => openChannelPage(event, cellValues.row.id)}
                        sx={{
                            color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                        }}
                    >
                        <OpenInNewIcon fontSize="small"/>
                    </IconButton>
                );
            }
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (cellValues) => {
                if(cellValues.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                return (
                    <RelationWebshopIntegrationCard
                        skeleton={cellValues.row.skeleton ?? false}
                        {...cellValues.row}
                        onClick={() => getWebshopCredentialDetails(cellValues)}
                    />
                )
            }
        }
    ];

    return (
        <Box
            sx={{
                height:1,
                "@media screen and (max-width: 47.9375em)" : {
                    paddingTop:0,
                    paddingX:0,
                }
            }}
        >
            <ShDataGrid3
                gridKey="relations.data.saleschannels"
                disableViewSaving
                key={theme.palette.mode}
                content={useGetWebshopCredentialsQuery}
                config={{relationId: props.relationId, integrator: -1}}
                gridActions={<GridActions></GridActions>}
                title={t("saleschannels")}
                gridOptions={gridOptions}
                sortModel={{field: columns[0].field,type:"ASC"}}
                columns={columns}
                onRowClick={getWebshopCredentialDetails}
                {...(hasModifyPrivilege && {
                    selectable: true,
                    setSelected: setSelectedItems,
                    selectedItems: selectedItems,
                    // A channel is only ever waiting to go live, so concept rows are the only
                    // ones that offer a checkbox. Integrator channels are activated on the
                    // integrator's side, so they stay out of the bulk action.
                    isRowSelectable: (row) => row.state === general_states.CONCEPT && !row.integrator,
                    selectedActions: (
                        <ShDataGridSelectedActions selectedItems={selectedItems}>
                            <DropdownButton
                                onClick={handleActivateSelected}
                                icon={<CheckCircleOutlineIcon/>}
                                text={t("activate_selected")}
                                disabled={isBulkUpdating}
                            />
                        </ShDataGridSelectedActions>
                    )
                })}
            >
            </ShDataGrid3>

            {spLoading &&
                <SpWebshopCredentialsDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    webshopCredentialsId={webshopCredentialsId}
                />
            }
            {spLoading &&
                <SpEtailizeWebshopDetails
                    isExtended={isExtendedEtailize}
                    setIsExtended={setIsExtendedEtailize}
                    etailizeWebshopId={webshopCredentialsId}
                />
            }

            <MLoader
                open={isUpdating}
                message={t("updating_saleschannels")}
            />
        </Box>
    )
};

export default Saleschannels;
